import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Authentication layout components
import CustomBasicLayout from "layouts/auth/components/CustomBasicLayout";

// Services
import AuthService from "services/auth-service";

function ResetPasswordPage() {
  const { resetId } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: ""
  });
  
  const [errors, setErrors] = useState({
    passwordError: false,
    confirmPasswordError: false,
    generalError: ""
  });
  
  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    // Validate that we have the required parameters
    if (!resetId) {
      setErrors(prev => ({
        ...prev,
        generalError: "Invalid password reset link. Please request a new password reset."
      }));
    }
  }, [resetId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors when user starts typing
    if (name === "password") {
      setErrors(prev => ({ ...prev, passwordError: false, generalError: "" }));
    } else if (name === "confirmPassword") {
      setErrors(prev => ({ ...prev, confirmPasswordError: false, generalError: "" }));
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = { passwordError: false, confirmPasswordError: false, generalError: "" };

    if (formData.password.length < 8) {
      newErrors.passwordError = true;
      isValid = false;
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPasswordError = true;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors(prev => ({ ...prev, generalError: "" }));

    try {
      await AuthService.resetPasswordWithOperationId(resetId, formData.password);
      setResetSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err) {
      setResetSuccess(false);
      // Handle different error types with user-friendly messages
      if (err.response && err.response.status === 404) {
        setErrors(prev => ({
          ...prev,
          generalError: "Password reset link is invalid or expired. Please request a new password reset."
        }));
      } else if (err.response && err.response.status === 400) {
        // Check for specific error messages from the backend
        if (err.response.data && err.response.data.message) {
          if (err.response.data.message.includes("Operation ID not found")) {
            setErrors(prev => ({
              ...prev,
              generalError: "Password reset link is invalid or expired. Please request a new password reset."
            }));
          } else if (err.response.data.message.includes("Invalid operation ID for this user")) {
            setErrors(prev => ({
              ...prev,
              generalError: "This password reset link is not valid for your account. Please request a new one."
            }));
          } else if (err.response.data.message.includes("Invalid operation ID")) {
            setErrors(prev => ({
              ...prev,
              generalError: "Password reset link is invalid. Please request a new password reset."
            }));
          } else {
            setErrors(prev => ({
              ...prev,
              generalError: "Invalid password reset request. Please try again."
            }));
          }
        } else {
          setErrors(prev => ({
            ...prev,
            generalError: "Invalid password reset request. Please try again."
          }));
        }
      } else {
        setErrors(prev => ({
          ...prev,
          generalError: "An unexpected error occurred during password reset. Please try again."
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRedirect = () => {
    navigate("/login");
  };

  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 3,
      backgroundColor: "#f8f9fa",
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: "#465475" },
      "&.Mui-focused fieldset": { borderColor: "#465475", borderWidth: 2 },
    },
  };

  return (
    <CustomBasicLayout>
      <Card sx={{ maxWidth: 420, width: "100%", p: 4, borderRadius: 3 }}>
        {/* Logo/Icon */}
        <MDBox display="flex" flexDirection="column" alignItems="center" mb={3}>
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={80}
            height={80}
            borderRadius="50%"
            sx={{
              background: "linear-gradient(135deg, #5a6a94 0%, #465475 100%)",
              boxShadow: "0 4px 20px rgba(70, 84, 117, 0.3)",
              mb: 2,
            }}
          >
            <Icon sx={{ fontSize: 36, color: "white" }}>
              {loading ? "hourglass_empty" : resetSuccess ? "check_circle" : "lock_reset"}
            </Icon>
          </MDBox>
          <MDTypography variant="h4" fontWeight="bold" color="dark">
            {loading ? "Resetting Password" : resetSuccess ? "Password Reset Successful" : "Reset Your Password"}
          </MDTypography>
        </MDBox>

        {/* Message Content */}
        <MDBox textAlign="center" mb={3}>
          {!resetId && (
            <MDTypography variant="body1" color="text" sx={{ mb: 2 }}>
              Invalid password reset link. Please request a new password reset.
            </MDTypography>
          )}

          {resetSuccess && (
            <>
              <MDTypography variant="body1" color="text" sx={{ mb: 2 }}>
                Your password has been successfully reset!
              </MDTypography>
              <MDTypography variant="body1" color="text" sx={{ mb: 2 }}>
                You can now log in with your new password.
              </MDTypography>
              <MDTypography variant="body2" color="text" sx={{ fontStyle: "italic" }}>
                Redirecting to login page...
              </MDTypography>
            </>
          )}

          {errors.generalError && !resetSuccess && (
            <MDBox mb={2} p={1.5} borderRadius={2} sx={{ backgroundColor: "#fee2e2" }}>
              <MDTypography variant="body1" color="error" fontWeight="medium">
                {errors.generalError}
              </MDTypography>
            </MDBox>
          )}
        </MDBox>

        {/* Form */}
        {!resetSuccess && resetId && (
          <MDBox component="form" onSubmit={handleSubmit}>
            {/* New Password */}
            <MDBox mb={2}>
              <MDTypography variant="button" fontWeight="medium" color="text" display="block" mb={1}>
                New Password
              </MDTypography>
              <TextField
                fullWidth
                type="password"
                name="password"
                placeholder="Enter new password"
                value={formData.password}
                onChange={handleInputChange}
                error={errors.passwordError}
                helperText={errors.passwordError ? "Password must be at least 8 characters" : ""}
                sx={inputStyles}
              />
            </MDBox>

            {/* Confirm Password */}
            <MDBox mb={2}>
              <MDTypography variant="button" fontWeight="medium" color="text" display="block" mb={1}>
                Confirm Password
              </MDTypography>
              <TextField
                fullWidth
                type="password"
                name="confirmPassword"
                placeholder="Confirm new password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                error={errors.confirmPasswordError}
                helperText={errors.confirmPasswordError ? "Passwords do not match" : ""}
                sx={inputStyles}
              />
            </MDBox>

            {/* Submit Button */}
            <MDButton
              variant="gradient"
              color="info"
              fullWidth
              size="large"
              type="submit"
              disabled={loading}
              sx={{
                borderRadius: 3,
                py: 1.5,
                textTransform: "none",
                fontSize: "1rem",
              }}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </MDButton>
          </MDBox>
        )}

        {/* Action Button */}
        <MDButton
          variant="outlined"
          color="info"
          fullWidth
          size="large"
          onClick={handleRedirect}
          sx={{
            borderRadius: 3,
            py: 1.5,
            textTransform: "none",
            fontSize: "1rem",
            mt: 2,
          }}
        >
          Back to Login
        </MDButton>
      </Card>
    </CustomBasicLayout>
  );
}

export default ResetPasswordPage;